import { ItemState, poBuckets } from '@/app-constants';
import { IConfirmedItemsState } from './interfaces/confirmed-items-state';
import { Reducer } from 'redux';
import * as Actions from './actions';
import { CREATE_CHANGE_REQUEST } from './components/create-change-request/create-change-request-actions';
import { IPoApprovePoState } from './interfaces/po-approve-po-state';
import { IPoDetailsBucketTotalsState } from './interfaces/po-details-bucket-totals-state';
import { IPoDetailsState } from './interfaces/po-details-state';
import { IPoItemsState } from './interfaces/po-items-state';
import { SUPPLIER_CONFIRM_CHANGE_REQUEST, CHANGE_REQUESTS_LIST_ACTIONS } from './components/change-requests/change-requests-actions';
import { CREATE_CONVERSATION_ACTIONS } from '@/app/common/conversation/actions';
import { HOLD_REQUESTS_LIST_ACTIONS } from './components/hold-requests/hold-requests-actions';
import { ItemStateEnum } from './components/change-requests/ItemStateEnum';

const approvePoInitialState: IPoApprovePoState = {
  error: '',
  approveSelectedItemsSuccess: false,
  loading: false,
  successfullyPostedNewPos: false,
  totalCount: 0
};

export const poApprovePoReducer: Reducer<IPoApprovePoState> = (state: IPoApprovePoState = approvePoInitialState, action) => {
  switch (action.type) {
    case Actions.APPROVE_PO_ACTIONS.REQUEST:
      return { ...state, loading: true };

    case Actions.APPROVE_PO_ACTIONS.SUCCESS:
      return {
        ...state,
        error: '',
        loading: false,
        successfullyPostedNewPos: true,
        totalCount: action.response.totalCount
      };

    case Actions.APPROVE_PO_ACTIONS.FAILURE:
      return { ...state, loading: false, error: action.error };

    case Actions.PO_ITEMS_ACTIONS.CONFIRM_SELECTED_SUCCESS:
      return { ...state, approveSelectedItemsSuccess: true, loading: false, error: '' };
    default:
      return state;
  }
};

const detailsInitialState: IPoDetailsState = {
  error: '',
  conversationId: 0,
  state: null,
  createdDate: null,
  customerCode: null,
  customerName: null,
  customerPoNum: null,
  earliestRequestedBookingEndDate: null,
  earliestRequestedDeliveryEndDate: null,
  earliestRequestedPickupEndDate: null,
  earliestRequestedShipEndDate: null,
  earliestAcknowledgementSentDate: null,
  earliestExpectedAcknowledgementDate: null,
  hasFlaggedConversations: false,
  hasUnreadMessages: false,
  loading: false,
  onHold: false,
  purchaseOrderId: 0,
  searchText: '',
  supplierCode: null,
  supplierName: null,
};

export const poDetailsReducer: Reducer<IPoDetailsState> = (state: IPoDetailsState = detailsInitialState, action) => {
  switch (action.type) {
    case Actions.PO_DETAILS_ACTIONS.REQUEST:
      return { ...state, conversationId: 0, loading: true };

    case Actions.PO_DETAILS_ACTIONS.SUCCESS:
      return { loading: false, ...action.payload, error: '' };

    case Actions.PO_DETAILS_ACTIONS.FAILURE:
      return { ...state, loading: false, error: action.error };

    case CREATE_CONVERSATION_ACTIONS.SUCCESS:
      return { ...state, conversationId: action.conversationId };

    case Actions.PO_DETAILS_ACTIONS.CLEAR:
      return { ...state, conversationId: 0, searchText: '' };

    case Actions.PO_ITEMS_ACTIONS.SEARCH:
      return { ...state, searchText: action.searchText };

    case Actions.PO_DETAILS_ACTIONS.REMOVE_UNREAD:
      return { ...state, hasUnreadMessages: false };

    case Actions.PO_DETAILS_ACTIONS.FOLLOWUP_DELETE_SUCCESS:
      return { ...state, hasFlaggedConversations: false };

    case Actions.PO_DETAILS_ACTIONS.FOLLOWUP_PUT_SUCCESS:
      return { ...state, hasFlaggedConversations: true };

    default:
      return state;
  }
};

const detailsBucketTotalsInitialState: IPoDetailsBucketTotalsState = {
  acceptedTotal: 0,
  activeBucketId: 0,
  buyerReviewCountWithUnreadMessages: 0,
  buyerReviewTotal: 0,
  canceledTotal: 0,
  shippedTotal: 0,
  error: null,
  holdRequestTotal: 0,
  holdRequestCountWithUnreadMessages: 0,
  loading: false,
  newTotal: 0,
  revisedTotal: 0,
  supplierReviewCountWithUnreadMessages: 0,
  supplierReviewTotal: 0
};

export const poDetailsBucketTotalsReducer: Reducer<IPoDetailsBucketTotalsState> = (state: IPoDetailsBucketTotalsState = detailsBucketTotalsInitialState, action) => {
  switch (action.type) {
    case Actions.PO_DETAILS_BUCKET_TOTALS_ACTIONS.REQUEST:
      return { ...state, loading: true };

    case Actions.PO_DETAILS_BUCKET_TOTALS_ACTIONS.SUCCESS:
      return {
        ...state,
        loading: false,
        error: '',
        ...action.response
      };

    case Actions.PO_DETAILS_BUCKET_TOTALS_ACTIONS.FAILURE:
      return { ...state, loading: false, error: action.error };

    case CREATE_CHANGE_REQUEST.SUCCESS:
      if (action.payload.stateId === ItemState.New) {
        return { ...state, newTotal: state.newTotal - 1, buyerReviewTotal: state.buyerReviewTotal + 1 };
      }

      if (action.payload.stateId === ItemState.Revised) {
        return { ...state, revisedTotal: state.revisedTotal - 1, buyerReviewTotal: state.buyerReviewTotal + 1 };
      }
      return { ...state, acceptedTotal: state.acceptedTotal - 1, buyerReviewTotal: state.buyerReviewTotal + 1 };

    case SUPPLIER_CONFIRM_CHANGE_REQUEST.SUCCESS:
      return { ...state, acceptedTotal: state.acceptedTotal + action.payload.purchaseOrderItemCount, supplierReviewTotal: state.supplierReviewTotal - 1 };

    case CHANGE_REQUESTS_LIST_ACTIONS.SUCCESS:
      if (state.activeBucketId === poBuckets['supplier-confirmation'].bucketTypeId) {
        const supplierReviewCountWithUnreadMessages = state.supplierReviewCountWithUnreadMessages ? state.supplierReviewCountWithUnreadMessages : action.payload.items.filter(cr => cr.hasUnreadMessages).length;
        return { ...state, supplierReviewCountWithUnreadMessages, supplierReviewTotal: action.payload.total };
      }
      if (state.activeBucketId === poBuckets['buyer-review'].bucketTypeId) {
        const buyerReviewCountWithUnreadMessages = state.buyerReviewCountWithUnreadMessages ? state.buyerReviewCountWithUnreadMessages : action.payload.items.filter(cr => cr.hasUnreadMessages).length;
        return { ...state, buyerReviewCountWithUnreadMessages, buyerReviewTotal: action.payload.total };
      }
      return state;

    case CHANGE_REQUESTS_LIST_ACTIONS.CANCELED:
      if (action.payload.itemState === ItemStateEnum.New) {
        return { ...state, newTotal: state.newTotal + action.payload.purchaseOrderItemCount, buyerReviewTotal: state.buyerReviewTotal - 1 };
      }
      return { ...state, acceptedTotal: state.acceptedTotal + action.payload.purchaseOrderItemCount, buyerReviewTotal: state.buyerReviewTotal - 1 };

    case CHANGE_REQUESTS_LIST_ACTIONS.REMOVE_UNREAD:

    case HOLD_REQUESTS_LIST_ACTIONS.REMOVE_UNREAD:
      return { ...state, ...decrementUnreadCounts(state) };

    case Actions.PO_DETAILS_ACTIONS.REMEMBER_TAB:
      return { ...state, activeBucketId: action.bucketTypeId };

    case Actions.PO_ITEMS_ACTIONS.CONFIRM_SELECTED_SUCCESS:
      const bucketType = action.payload.bucketType;
      const newState = updateCountsAfterConfirmingItems(bucketType, state, action.payload);
      return newState;

    case Actions.HOLD_REQUEST.CREATE_SUCCESS:
      let itemCount = action.payload.excludedPurchaseOrderItemIds && action.payload.excludedPurchaseOrderItemIds.length;
      if (!itemCount) {
        itemCount = state.newTotal - (action.payload.purchaseOrderItemIds && action.payload.purchaseOrderItemIds.length);
      }

      return {
        ...state,
        holdRequestTotal: state.holdRequestTotal + 1,
        newTotal: itemCount
      };

    case Actions.HOLD_REQUEST.RELEASE_SUCCESS:
      return {
        ...state,
        holdRequestTotal: state.holdRequestTotal - 1,
        newTotal: state.newTotal + action.payload.purchaseOrderItemCount
      };

    default:
      return state;
  }
};

const updateCountsAfterConfirmingItems = (bucketType: string, currentState: IPoDetailsBucketTotalsState, payload: any): Partial<IPoDetailsBucketTotalsState> => {
  if (bucketType === poBuckets.new.state) {
    const newTotal = payload.excludedPurchaseOrderItemIds.length
      ? payload.excludedPurchaseOrderItemIds.length
      : currentState.newTotal - (payload.purchaseOrderItemIds.length || currentState.newTotal);

    const acceptedTotal = payload.excludedPurchaseOrderItemIds.length
      ? currentState.acceptedTotal + currentState.newTotal - payload.excludedPurchaseOrderItemIds.length
      : currentState.acceptedTotal + (payload.purchaseOrderItemIds.length || currentState.newTotal);

    return { ...currentState, acceptedTotal, newTotal };
  }
  if (bucketType === poBuckets.revised.state) {
    const revisedTotal = payload.excludedPurchaseOrderItemIds.length
      ? payload.excludedPurchaseOrderItemIds.length
      : currentState.revisedTotal - (payload.purchaseOrderItemIds.length || currentState.revisedTotal);

    const acceptedTotal = payload.excludedPurchaseOrderItemIds.length
      ? currentState.acceptedTotal + currentState.revisedTotal - payload.excludedPurchaseOrderItemIds.length
      : currentState.acceptedTotal + (payload.purchaseOrderItemIds.length || currentState.revisedTotal);

    return { ...currentState, acceptedTotal, revisedTotal };
  }

  return currentState;
};

const decrementUnreadCounts = (state: IPoDetailsBucketTotalsState): Partial<IPoDetailsBucketTotalsState> => {
  if (state.activeBucketId === poBuckets['on-hold'].bucketTypeId) {
    return { holdRequestCountWithUnreadMessages: state.holdRequestCountWithUnreadMessages - 1 };
  }
  else if (state.activeBucketId === poBuckets['buyer-review'].bucketTypeId) {
    return { buyerReviewCountWithUnreadMessages: state.buyerReviewCountWithUnreadMessages - 1 };
  }
  else if (state.activeBucketId === poBuckets['supplier-confirmation'].bucketTypeId) {
    return { supplierReviewCountWithUnreadMessages: state.supplierReviewCountWithUnreadMessages - 1 };
  }
};

const poItemsInitialState: IPoItemsState = {
  currentPage: 1,
  totalItemCount: 0,
  poItems: [],
  error: '',
  loading: false
};

export const poItemsReducer: Reducer<IPoItemsState> = (state: IPoItemsState = poItemsInitialState, action) => {
  switch (action.type) {
    case Actions.PO_ITEMS_ACTIONS.REQUEST:
      return { ...state, loading: true };

    case Actions.PO_ITEMS_ACTIONS.SUCCESS:
      return {
        ...state,
        poItems: [...state.poItems, ...action.payload.purchaseOrderItems],
        loading: false,
        totalItemCount: action.payload.totalCount,
        currentPage: action.payload.currentPage
      };

    case Actions.HOLD_REQUEST.CREATE_SUCCESS:
    case Actions.PO_ITEMS_ACTIONS.FILTER:
      let newItems = [];
      if (action.payload.excludedPurchaseOrderItemIds && action.payload.excludedPurchaseOrderItemIds.length > 0) {
        newItems = state.poItems.filter(item => action.payload.excludedPurchaseOrderItemIds.includes(item.purchaseOrderItemId));
      } else {
        newItems = state.poItems.filter(item => !action.payload.purchaseOrderItemIds.includes(item.purchaseOrderItemId));
      }

      return { ...state, poItems: newItems };

    case Actions.PO_ITEMS_ACTIONS.FAILURE:
      return { ...state, loading: false, error: action.error };

    case CREATE_CHANGE_REQUEST.SUCCESS:
      if (action.payload.stateId === ItemState.New) {
        const newItems = state.poItems.filter(item => item.purchaseOrderItemId !== action.payload.purchaseOrderItemId);
        return { ...state, poItems: newItems };
      }

      return state;

    case Actions.PO_ITEMS_ACTIONS.SEARCH:
    case Actions.PO_DETAILS_ACTIONS.CLEAR:
      return { ...state, poItems: [], currentPage: 1 };

    case CHANGE_REQUESTS_LIST_ACTIONS.CANCELED:
      if (action.payload.itemState === ItemStateEnum.New) {
        return { ...state, poItems: [], currentPage: 1 };
      }
      return state;

    case Actions.PO_ITEMS_ACTIONS.CONFIRM_SELECTED_SUCCESS:
      let itemsToKeep = [];
      if (action.payload.excludedPurchaseOrderItemIds.length) {
        itemsToKeep = state.poItems.filter(item => action.payload.excludedPurchaseOrderItemIds.includes(item.purchaseOrderItemId));
      } else if (action.payload.purchaseOrderItemIds.length) {
        itemsToKeep = state.poItems.filter(item => !action.payload.purchaseOrderItemIds.includes(item.purchaseOrderItemId));
      }

      return {
        ...state,
        poItems: itemsToKeep
      };

    default:
      return state;
  }
};

const confirmedItemsInitialState: IConfirmedItemsState = {
  currentPage: 1,
  totalItemCount: 0,
  items: [],
  error: '',
  loading: false
};

export const confirmedPoItemsReducer: Reducer<IConfirmedItemsState> = (state: IConfirmedItemsState = confirmedItemsInitialState, action) => {
  switch (action.type) {
    case Actions.CONFIRMED_PO_ITEMS_ACTIONS.FAILURE:
      return { ...state, loading: false, error: action.error };

    case Actions.CONFIRMED_PO_ITEMS_ACTIONS.REQUEST:
      return { ...state, loading: true };

    case Actions.CONFIRMED_PO_ITEMS_ACTIONS.SUCCESS:
      return {
        ...state,
        items: [...state.items, ...action.payload.purchaseOrderItems],
        loading: false,
        totalItemCount: action.payload.totalCount,
        currentPage: action.payload.currentPage
      };

    case Actions.CONFIRMED_PO_ITEMS_ACTIONS.FILTER:
      let confirmedItems = [];
      if (action.payload.excludedPurchaseOrderItemIds) {
        confirmedItems = state.items.filter(item => action.payload.excludedPurchaseOrderItemIds.includes(item.purchaseOrderItemId));
      } else {
        confirmedItems = state.items.filter(item => !action.payload.purchaseOrderItemIds.includes(item.purchaseOrderItemId));
      }

      return { ...state, items: confirmedItems };

    case CREATE_CHANGE_REQUEST.SUCCESS:
      if (action.payload.stateId === ItemState.Accepted) {
        const newItems = state.items.filter(item => item.purchaseOrderItemId !== action.payload.purchaseOrderItemId);
        return { ...state, items: newItems };
      }

      return state;

    case SUPPLIER_CONFIRM_CHANGE_REQUEST.SUCCESS:
    case Actions.PO_ITEMS_ACTIONS.CONFIRM_SELECTED_SUCCESS:
    case Actions.PO_ITEMS_ACTIONS.SEARCH:
    case Actions.PO_DETAILS_ACTIONS.CLEAR:
      return { ...state, items: [], currentPage: 1 };

    case CHANGE_REQUESTS_LIST_ACTIONS.CANCELED:
      if (action.payload.itemState === ItemStateEnum.Accepted) {
        return { ...state, items: [], currentPage: 1 };
      }
      return state;

    default:
      return state;
  }
};
